import { useCallback } from 'react';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import { type FeedOptions, type FollowedUser, DEFAULT_FEED_OPTIONS, MAX_FOLLOWS } from '@/lib/feed';

export type AddResult = 'added' | 'duplicate' | 'full';

/** LocalStorage-backed list of followed npubs plus feed options. */
export function useFollowedNpubs() {
  const [users, setUsers] = useLocalStorage<FollowedUser[]>('nostrss:follows', []);
  const [options, setOptions] = useLocalStorage<FeedOptions>('nostrss:options', DEFAULT_FEED_OPTIONS);

  const addUser = useCallback(
    (npub: string, pubkey: string): AddResult => {
      if (users.some((user) => user.pubkey === pubkey)) {
        return 'duplicate';
      }
      if (users.length >= MAX_FOLLOWS) {
        return 'full';
      }
      setUsers((prev) => [...prev, { npub, pubkey, addedAt: Date.now() }]);
      return 'added';
    },
    [users, setUsers],
  );

  const removeUser = useCallback(
    (pubkey: string) => {
      setUsers((prev) => prev.filter((user) => user.pubkey !== pubkey));
    },
    [setUsers],
  );

  return { users, addUser, removeUser, options, setOptions };
}
